import {
  Box,
  Typography,
  LinearProgress,
  Button,
  Stack,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React from 'react';
import { Lock, Check, Share as ShareIcon } from '@mui/icons-material';
import AlbumShareButton from '../AlbumShareButton';
import { track } from '../../analytics';

interface AlbumHeaderProps {
  petName: string;
  petType: 'dog' | 'cat';
  completed: number;
  total: number;
  photoCount: number;
  albumId: string;
  albumName: string;
  species: string;
  petId: string;
  onCopySuccess: () => void;
}

function AlbumHeader({
  petName,
  petType,
  completed,
  total,
  photoCount,
  albumId,
  albumName,
  species,
  petId,
  onCopySuccess,
}: AlbumHeaderProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sharePath = `/albums/share/${petId}/${albumId}`;
  const shareUrl = `${window.location.origin}${sharePath}`;
  const shareProps = { album_name: albumName, species };
  const handlePreview = () => {
    track('album_preview_click', shareProps);
    window.open(sharePath, '_blank', 'noopener,noreferrer');
  };
  const progress = (completed / total) * 100;
  const petEmoji = petType === 'dog' ? '🐶' : '🐱';
  const minPromptsForUnlock = 3;
  const canPreviewOrShare = completed >= minPromptsForUnlock;
  const allPromptsComplete = completed === total;

  return (
    <Box
      sx={{
        pb: 3,
        pt: 3,
      }}
    >
      <Stack spacing={1.5}>
        <Stack
          spacing={3}
          alignItems={{ xs: 'start', lg: 'center' }}
          direction={{ xs: 'column', lg: 'row' }}
        >
          <Stack sx={{ width: '100%' }}>
            <Stack
              spacing={1.5}
              alignItems={{ xs: 'start', md: 'center' }}
              direction={{ xs: 'column', md: 'row' }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: '20px' }}>{petEmoji}</Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 500,
                    fontSize: '16px',
                    lineHeight: '20px',
                  }}
                >
                  {petName}
                </Typography>
              </Stack>

              <Box
                sx={{
                  width: 4,
                  height: 4,
                  borderRadius: '50%',
                  backgroundColor: 'text.secondary',
                  display: {
                    xs: 'none',
                    md: 'block',
                  },
                }}
              />

              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                sx={{ height: '24px' }}
              >
                {!allPromptsComplete ? (
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontSize: '14px',
                      lineHeight: '16px',
                    }}
                  >
                    {`${completed} of ${total} prompts completed`}
                  </Typography>
                ) : (
                  <Chip
                    icon={<Check />}
                    label={`All ${total} prompts completed`}
                    sx={{
                      backgroundColor: '#FFE0B2',
                      color: '#000',
                      fontWeight: 500,
                      fontSize: '14px',
                      height: '24px',
                      borderRadius: '16px',
                      width: 'fit-content',
                      '& .MuiChip-icon': {
                        color: '#000',
                        fontSize: '18px',
                      },
                    }}
                  />
                )}

                <Box
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    backgroundColor: 'text.secondary',
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    fontSize: '14px',
                    lineHeight: '16px',
                  }}
                >
                  {`${photoCount} photos`}
                </Typography>
              </Stack>
            </Stack>

            {!allPromptsComplete && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 6,
                    borderRadius: '10px',
                    backgroundColor: '#e8e1f4',
                    mt: 1,
                    '& .MuiLinearProgress-bar': {
                      borderRadius: '10px',
                      backgroundColor: 'primary.main',
                    },
                  }}
                />
              </Box>
            )}
          </Stack>
          {canPreviewOrShare && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{ width: { xs: '100%', lg: 'auto' } }}
            >
              <Button
                variant="outlined"
                color="inherit"
                onClick={handlePreview}
                sx={{
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  flexGrow: { xs: 1, lg: 0 },
                  width: { xs: 'auto', lg: 200 },
                  border: '1px solid',
                  borderColor: 'rgba(0, 0, 0, 0.87)',
                  color: 'text.primary',
                  '&:hover': {
                    borderColor: 'rgba(0, 0, 0, 0.87)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                Preview Album
              </Button>
              <AlbumShareButton
                icon={<ShareIcon sx={{ fontSize: 24 }} />}
                ariaLabel="share album"
                url={shareUrl}
                useNativeShare={isMobile}
                onCopySuccess={onCopySuccess}
                onShareClick={() => track('album_share_click', shareProps)}
                onCopyClick={() => track('copy_album_click', shareProps)}
                onFacebookClick={() => track('facebook_album_click', shareProps)}
                buttonSx={{
                  flexShrink: 0,
                  width: 42,
                  height: 42,
                  borderRadius: '8px',
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  boxShadow:
                    '0px 1px 5px 0px rgba(0,0,0,0.12), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 3px 1px -2px rgba(0,0,0,0.2)',
                  '&:hover': {
                    backgroundColor: 'primary.dark',
                  },
                }}
              />
            </Stack>
          )}
        </Stack>

        {!canPreviewOrShare && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Lock sx={{ fontSize: '24px', color: 'primary.main' }} />
            <Typography
              variant="body2"
              sx={{
                color: 'primary.main',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '16px',
              }}
            >
              {`Complete ${minPromptsForUnlock} prompts to preview and share album`}
            </Typography>
          </Stack>
        )}
      </Stack>
    </Box>
  );
}

export default AlbumHeader;
